const BASE_URL = '/api';

export interface OAuthClientRow {
  readonly client_id: string;
  readonly client_name: string;
  readonly redirect_uris: readonly string[];
  readonly created_at: string;
  readonly expires_at: string | null;
  /** Operator-verified client (SEC-004). */
  readonly is_verified: boolean;
  /** True when this tenant registered the client (SEC-004). */
  readonly registered_by_you: boolean;
  /** True when this tenant holds a live authorization for the client (SEC-004). */
  readonly has_active_tokens: boolean;
  readonly last_used_at: string | null;
  /** FEAT-010 lifecycle (server-computed). */
  readonly lifecycle_status: OAuthClientLifecycleStatus;
  /** When the 7-day grace period ends (null for non-expiring clients). */
  readonly grace_ends_at: string | null;
  /** 30 / 7 / 1 when the owner should be warned the client expires soon. */
  readonly expiry_warning_days: 30 | 7 | 1 | null;
  /** True when this tenant registered the client and can renew it now. */
  readonly renewable: boolean;
}

export type OAuthClientLifecycleStatus = 'non_expiring' | 'active' | 'expiring' | 'in_grace' | 'expired';

export interface UsageRow {
  readonly usage_date: string;
  readonly tool_name: string;
  readonly request_count: number;
  readonly cache_hit_count: number;
  readonly error_count: number;
}

async function fetchWithAuth(path: string, options: RequestInit = {}): Promise<Response> {
  // Auth is added by the caller via getAuthHeaders
  return fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

export function getAuthHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

export async function getOAuthClients(token: string): Promise<Response> {
  return fetchWithAuth('/oauth/clients/list', {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
}

export interface CreateOAuthClientResponse {
  readonly client_id: string;
  readonly client_name: string;
  readonly redirect_uris: readonly string[];
  readonly registration_access_token: string;
  readonly registration_client_uri: string;
  readonly token_endpoint_auth_method: string;
  readonly grant_types: readonly string[];
  readonly response_types: readonly string[];
  readonly client_id_issued_at: number;
  readonly client_secret_expires_at: number;
}

export async function createOAuthClient(token: string, clientName: string, redirectUris: string[]): Promise<Response> {
  return fetchWithAuth('/oauth/register', {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ client_name: clientName, redirect_uris: redirectUris }),
  });
}

/** FEAT-010: extend a client you registered to now + 90 days. */
export async function renewOAuthClient(token: string, clientId: string): Promise<Response> {
  return fetchWithAuth(`/oauth/clients/${encodeURIComponent(clientId)}/renew`, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });
}

export async function revokeOAuthClient(token: string, clientId: string): Promise<Response> {
  return fetchWithAuth(`/oauth/clients/${encodeURIComponent(clientId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
  });
}

export async function createCheckoutSession(token: string, priceId: string): Promise<Response> {
  return fetchWithAuth('/billing/checkout-session', {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ price_id: priceId }),
  });
}

export async function createPortalSession(token: string): Promise<Response> {
  return fetchWithAuth('/billing/portal-session', {
    method: 'POST',
    headers: getAuthHeaders(token),
  });
}

export async function getRecentActivity(token: string, limit = 20): Promise<Response> {
  return fetchWithAuth(`/dashboard/recent-activity?limit=${limit}`, {
    headers: getAuthHeaders(token),
  });
}

export async function getUsageRows(token: string): Promise<Response> {
  return fetchWithAuth('/dashboard/usage', {
    headers: getAuthHeaders(token),
  });
}

export async function deleteAccount(token: string): Promise<Response> {
  return fetchWithAuth('/account/delete', {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ confirmation: 'DELETE' }),
  });
}

export interface ApiKeyRow {
  readonly key_id: string;
  readonly label: string;
  readonly created_at: string;
  readonly last_used_at: string | null;
  readonly is_active: boolean;
  readonly key_prefix: string;
  readonly rotation_deactivate_at: string | null;
}

export async function getApiKeys(token: string): Promise<Response> {
  return fetchWithAuth('/api-keys/list', {
    headers: getAuthHeaders(token),
  });
}

export async function createApiKey(token: string, label: string): Promise<Response> {
  return fetchWithAuth('/api-keys/create', {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ label }),
  });
}

export async function revokeApiKey(token: string, keyId: string): Promise<Response> {
  return fetchWithAuth(`/api-keys/${encodeURIComponent(keyId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
  });
}

export async function rotateApiKey(token: string, keyId: string): Promise<Response> {
  return fetchWithAuth(`/api-keys/${encodeURIComponent(keyId)}/rotate`, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });
}

// ── Intelligence workspace types ──────────────────────────────────────────────

export interface SourceCoverageRow {
  readonly source_id: string;
  readonly source_family: string;
  readonly agency: string;
  readonly jurisdiction: string;
  readonly document_type: string;
  readonly enabled: boolean;
  readonly tier_visibility: string;
  readonly document_count: number;
  readonly finding_count: number;
  readonly earliest_event_date: string | null;
  readonly latest_event_date: string | null;
  readonly last_document_ingested_at: string | null;
  readonly coverage_status: 'active' | 'stale' | 'no_documents';
}

export interface TrendMonthlyRow {
  readonly period_month: string;
  readonly source_family: string;
  readonly agency: string;
  readonly jurisdiction: string;
  readonly gmp_system: string | null;
  readonly finding_count: number;
  readonly document_count: number;
  readonly unique_firms: number;
  readonly earliest_event_date: string | null;
  readonly latest_event_date: string | null;
  readonly last_ingested_at: string | null;
}

export interface TrendGmpSystemRow {
  readonly gmp_system: string | null;
  readonly source_family: string;
  readonly agency: string;
  readonly jurisdiction: string;
  readonly finding_count: number;
  readonly document_count: number;
  readonly unique_firms: number;
  readonly earliest_event_date: string | null;
  readonly latest_event_date: string | null;
  readonly last_ingested_at: string | null;
}

export interface TrendRegulationRow {
  readonly citation_type: string;
  readonly source_family: string;
  readonly agency: string;
  readonly jurisdiction: string;
  readonly citation_count: number;
  readonly finding_count: number;
  readonly document_count: number;
  readonly earliest_event_date: string | null;
  readonly latest_event_date: string | null;
  readonly last_ingested_at: string | null;
}

export type TrendRow = TrendMonthlyRow | TrendGmpSystemRow | TrendRegulationRow;

export interface TrendsParams {
  readonly group_by?: 'month' | 'gmp_system' | 'agency' | 'regulation' | 'source_family';
  readonly metric?: 'finding_count' | 'unique_firms' | 'document_count' | 'citation_count';
  readonly agency?: string;
  readonly source_family?: string;
  readonly gmp_system?: string;
  readonly date_from?: string;
  readonly date_to?: string;
  readonly window_months?: number;
}

export async function getIntelligenceSources(token: string): Promise<Response> {
  return fetchWithAuth('/intelligence/sources', {
    headers: getAuthHeaders(token),
  });
}

export async function getIntelligenceTrends(token: string, params: TrendsParams = {}): Promise<Response> {
  const qs = new URLSearchParams();
  if (params.group_by) qs.set('group_by', params.group_by);
  if (params.metric) qs.set('metric', params.metric);
  if (params.agency) qs.set('agency', params.agency);
  if (params.source_family) qs.set('source_family', params.source_family);
  if (params.gmp_system) qs.set('gmp_system', params.gmp_system);
  if (params.date_from) qs.set('date_from', params.date_from);
  if (params.date_to) qs.set('date_to', params.date_to);
  if (params.window_months !== undefined) qs.set('window_months', String(params.window_months));
  const queryString = qs.toString();
  return fetchWithAuth(`/intelligence/trends${queryString ? `?${queryString}` : ''}`, {
    headers: getAuthHeaders(token),
  });
}

// ── Intelligence Documents ────────────────────────────────────────────────────

export interface DocumentRow {
  readonly source_document_id: string;
  readonly source_id: string | null;
  readonly agency: string | null;
  readonly jurisdiction: string | null;
  readonly document_type: string | null;
  readonly canonical_url: string | null;
  readonly title: string | null;
  readonly company_name_raw: string | null;
  readonly fei_number: string | null;
  readonly issue_date: string | null;
  readonly posted_date: string | null;
  readonly issuing_office: string | null;
  readonly fetch_status: string | null;
  readonly extract_status: string | null;
  readonly current_asset_sha256: string | null;
}

export interface DocumentDetail extends DocumentRow {
  readonly source_external_id: string | null;
  readonly facility_name_raw: string | null;
  readonly subject: string | null;
  readonly source_payload_hash: string | null;
  readonly source_disclaimer: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

export interface FindingRow {
  readonly finding_id: string;
  readonly finding_number: string | null;
  readonly finding_text: string | null;
  readonly evidence_excerpt: string | null;
  readonly gmp_system: string | null;
  readonly process_area: string | null;
  readonly risk_theme: string | null;
  readonly severity: string | null;
  readonly confidence_score: number | null;
  readonly extraction_method: string | null;
}

// Columns of regintel.regulatory_citations (BUG-047: there is no regulation_part /
// regulation_section / confidence_score — citation_text carries the reference).
export interface CitationRow {
  readonly citation_id: string;
  readonly finding_id: string | null;
  readonly citation_type: string | null;
  readonly citation_text: string | null;
  readonly created_at: string | null;
}

// Columns of regintel.source_document_assets (BUG-047: mime_type / created_at,
// not content_type / fetched_at).
export interface AssetRow {
  readonly asset_id: string;
  readonly asset_role: string | null;
  readonly r2_key: string | null;
  readonly mime_type: string | null;
  readonly content_sha256: string | null;
  readonly byte_size: number | null;
  readonly fetch_status: string | null;
  readonly created_at: string | null;
}

export interface DocumentDetailResponse {
  readonly document: DocumentDetail;
  readonly findings: readonly FindingRow[];
  readonly citations: readonly CitationRow[];
  readonly assets: readonly AssetRow[];
}

export interface DocumentsParams {
  readonly query?: string;
  readonly agency?: string;
  readonly source_family?: string;
  readonly document_type?: string;
  readonly fetch_status?: string;
  readonly extract_status?: string;
  readonly date_from?: string;
  readonly date_to?: string;
  readonly limit?: number;
  readonly offset?: number;
}

export async function getIntelligenceDocuments(token: string, params: DocumentsParams = {}): Promise<Response> {
  const qs = new URLSearchParams();
  if (params.query) qs.set('query', params.query);
  if (params.agency) qs.set('agency', params.agency);
  if (params.source_family) qs.set('source_family', params.source_family);
  if (params.document_type) qs.set('document_type', params.document_type);
  if (params.fetch_status) qs.set('fetch_status', params.fetch_status);
  if (params.extract_status) qs.set('extract_status', params.extract_status);
  if (params.date_from) qs.set('date_from', params.date_from);
  if (params.date_to) qs.set('date_to', params.date_to);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const queryString = qs.toString();
  return fetchWithAuth(`/intelligence/documents${queryString ? `?${queryString}` : ''}`, {
    headers: getAuthHeaders(token),
  });
}

export async function getIntelligenceDocumentDetail(token: string, documentId: string): Promise<Response> {
  return fetchWithAuth(`/intelligence/documents/${encodeURIComponent(documentId)}`, {
    headers: getAuthHeaders(token),
  });
}

// ── Intelligence Similarity ───────────────────────────────────────────────────

export interface SimilarityResult {
  readonly chunk_id: string;
  readonly source_document_id: string;
  readonly similarity: number;
  readonly confidence: 'high' | 'medium' | 'low';
  readonly chunk_type: string | null;
  readonly finding: {
    readonly finding_id: string | null;
    readonly finding_text: string | null;
    readonly gmp_system: string | null;
    readonly process_area: string | null;
    readonly severity: string | null;
  } | null;
  readonly document: {
    readonly source_document_id: string;
    readonly agency: string | null;
    readonly document_type: string | null;
    readonly canonical_url: string | null;
    readonly title: string | null;
    readonly issue_date: string | null;
    readonly posted_date: string | null;
  };
  readonly excerpt: string | null;
}

export interface SimilarityResponse {
  readonly results: readonly SimilarityResult[];
  readonly coverage_gaps: readonly { code: string; message: string }[];
  readonly meta: {
    readonly query_length_chars: number;
    readonly limit: number;
    readonly result_count: number;
  };
}

export interface SimilarityParams {
  readonly query_text: string;
  readonly limit?: number;
  readonly agency?: string;
  readonly gmp_system?: string;
  readonly date_from?: string;
  readonly date_to?: string;
}

export async function postIntelligenceSimilarity(token: string, params: SimilarityParams): Promise<Response> {
  return fetchWithAuth('/intelligence/similarity', {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(params),
  });
}

// ── Intelligence Graph ────────────────────────────────────────────────────────

export interface GraphNode {
  readonly id: string;
  readonly type: string;
  readonly label: string;
  readonly external_id: string;
}

export interface GraphEdge {
  readonly edge_id: string;
  readonly type: string;
  readonly from: string;
  readonly to: string;
  readonly confidence: number;
  readonly match_basis: string;
}

export interface GraphResponse {
  readonly graph: {
    readonly nodes: readonly GraphNode[];
    readonly edges: readonly GraphEdge[];
    readonly evidence: readonly { edge_id: string; excerpt: string; confidence: number }[];
    readonly warnings: readonly { code: string; message: string }[];
    readonly anchor_found: boolean;
    readonly node_count: number;
    readonly edge_count: number;
  } | null;
}

export interface GraphParams {
  readonly anchor_type: string;
  readonly anchor_value: string;
  readonly depth?: 1 | 2;
  readonly edge_types?: string;
  readonly date_from?: string;
  readonly date_to?: string;
}

export async function getIntelligenceGraph(token: string, params: GraphParams): Promise<Response> {
  const qs = new URLSearchParams({
    anchor_type: params.anchor_type,
    anchor_value: params.anchor_value,
  });
  if (params.depth !== undefined) qs.set('depth', String(params.depth));
  if (params.edge_types) qs.set('edge_types', params.edge_types);
  if (params.date_from) qs.set('date_from', params.date_from);
  if (params.date_to) qs.set('date_to', params.date_to);
  return fetchWithAuth(`/intelligence/graph?${qs.toString()}`, {
    headers: getAuthHeaders(token),
  });
}

// ── Intelligence Inspectors ───────────────────────────────────────────────────

export interface InspectorRow {
  readonly inspector_id: string;
  readonly canonical_name: string;
  readonly aliases: readonly string[] | null;
  readonly primary_office: string | null;
  readonly total_linked_inspections: number | null;
  readonly first_seen_at: string | null;
  readonly last_seen_at: string | null;
  readonly exposure_eligible: boolean;
}

export interface InspectorDetail {
  readonly inspector_id: string;
  readonly canonical_name: string;
  readonly aliases: readonly string[] | null;
  readonly title_roles: readonly string[] | null;
  readonly primary_office: string | null;
  readonly total_linked_inspections: number | null;
  readonly first_seen_at: string | null;
  readonly last_seen_at: string | null;
  readonly active_years: number | null;
  readonly profile_available: boolean;
  readonly reason?: string;
  readonly signals?: readonly Record<string, unknown>[];
  readonly correction_channel: string;
  readonly warnings: readonly { code: string; message: string }[];
}

export interface InspectorDetailResponse {
  readonly inspector: InspectorDetail;
}

export interface InspectorsParams {
  readonly query?: string;
  readonly limit?: number;
}

export async function getIntelligenceInspectors(token: string, params: InspectorsParams = {}): Promise<Response> {
  const qs = new URLSearchParams();
  if (params.query) qs.set('query', params.query);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const queryString = qs.toString();
  return fetchWithAuth(`/intelligence/inspectors${queryString ? `?${queryString}` : ''}`, {
    headers: getAuthHeaders(token),
  });
}

export async function getIntelligenceInspectorDetail(token: string, inspectorId: string): Promise<Response> {
  return fetchWithAuth(`/intelligence/inspectors/${encodeURIComponent(inspectorId)}`, {
    headers: getAuthHeaders(token),
  });
}
